import React, { useState } from 'react';
import { X, BellRing, ExternalLink, ShieldCheck } from 'lucide-react';
import { motion } from 'framer-motion';

export default function DonateModal({ isOpen, onClose }) {
  const [amount, setAmount] = useState("50");
  const [step, setStep] = useState(1); 
  const [isMonthly, setIsMonthly] = useState(false);

  // Link to your personal PayPal.me
  const paypalMeBase = "https://paypal.me/EricTwambale";

  const handleFinalRedirect = () => {
    // This opens your PayPal.me with the specific amount pre-filled
    // Format: paypal.me/username/amount
    window.open(`${paypalMeBase}/${amount}`, '_blank');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center px-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-slate-900/95 backdrop-blur-md" onClick={onClose}></div>
      
      {/* Modal Card */}
      <div className="relative bg-white w-full max-w-md rounded-[2.5rem] overflow-hidden shadow-2xl animate-in zoom-in duration-300">
        <button onClick={onClose} className="absolute top-6 right-6 text-gray-400 hover:text-gh-blue z-30 transition-colors">
          <X size={28} />
        </button>

        <div className="p-8 md:p-10">
          {/* MINISTRY LOGO */}
          <div className="flex justify-center mb-6">
            <img 
              src="/Giving-logo.png" 
              alt="Giving Hands Ministry Logo" 
              className="h-20 w-auto object-contain" 
            />
          </div>
          
          <h3 className="text-2xl font-black text-center text-gh-blue mb-2 uppercase italic tracking-tighter">Support a Child</h3>
          <p className="text-center text-gray-500 mb-8 text-sm px-4 leading-relaxed">
            Your generosity provides food, medical care, and hope to the children of <span className="text-gh-blue font-bold">Giving Hands Ministry</span>.
          </p>

          {step === 1 ? (
            <div className="space-y-6">
              <div>
                <label className="block text-[10px] font-black uppercase tracking-[0.2em] text-gray-400 mb-4 text-center">Select Amount (USD)</label>
                <div className="grid grid-cols-3 gap-3">
                  {["25", "50", "100"].map((val) => (
                    <button 
                      key={val}
                      onClick={() => setAmount(val)}
                      className={`py-4 rounded-2xl font-black transition-all duration-300 ${amount === val ? 'bg-gh-blue text-white shadow-xl scale-105' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'}`}
                    >
                      ${val}
                    </button>
                  ))}
                </div>
              </div>

              <div className="relative">
                <span className="absolute left-5 top-1/2 -translate-y-1/2 text-gh-blue font-black text-xl">$</span>
                <input 
                  type="number" 
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="w-full pl-10 pr-6 py-5 bg-slate-50 border-2 border-transparent focus:border-gh-orange/20 rounded-[1.5rem] outline-none font-black text-gh-blue text-xl transition-all"
                  placeholder="Custom Amount"
                />
              </div>

              {/* MONTHLY DONATION CHECKBOX */}
              <div 
                className="bg-slate-50 p-5 rounded-[1.5rem] border border-slate-100 group cursor-pointer" 
                onClick={() => setIsMonthly(!isMonthly)}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-all duration-300 ${isMonthly ? 'bg-gh-orange border-gh-orange shadow-inner' : 'border-slate-300'}`}>
                    {isMonthly && <div className="w-2 h-2 bg-white rounded-full"></div>}
                  </div>
                  <span className={`text-sm font-bold transition-colors ${isMonthly ? 'text-gh-blue' : 'text-slate-500'}`}>
                    Make this a monthly donation
                  </span>
                </div>
                {isMonthly && (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="mt-4 flex items-start gap-3 text-[11px] text-gh-blue/70 font-medium leading-tight border-t border-slate-200 pt-4"
                  >
                    <BellRing size={16} className="mt-0.5 flex-shrink-0 text-gh-orange" />
                    <p>We'll send you a friendly reminder every month to renew your support for the children via PayPal.</p>
                  </motion.div>
                )}
              </div>

              <button 
                onClick={() => setStep(2)}
                disabled={!amount || amount <= 0}
                className="w-full bg-gh-blue text-white py-5 rounded-[1.5rem] font-black uppercase tracking-widest hover:bg-blue-900 transition-all shadow-2xl disabled:opacity-50 disabled:cursor-not-allowed active:scale-95 flex items-center justify-center gap-2"
              >
                Continue to Donate
              </button>
            </div>
          ) : (
            <div className="animate-in slide-in-from-right duration-500 space-y-6">
               <div className="p-6 bg-gh-blue rounded-[2rem] flex justify-between items-center text-white shadow-xl relative overflow-hidden">
                  <div className="relative z-10">
                    <span className="text-[10px] font-bold uppercase tracking-[0.2em] block opacity-70 mb-1">Your Contribution</span>
                    <span className="text-4xl font-black">${amount}</span>
                  </div>
                  {isMonthly && (
                    <span className="relative z-10 bg-gh-orange text-[10px] font-black px-4 py-2 rounded-full uppercase tracking-widest shadow-lg">
                      Monthly
                    </span>
                  )}
                  {/* Decorative Circle */}
                  <div className="absolute -right-4 -bottom-4 w-20 h-20 bg-white/10 rounded-full"></div>
               </div>

               <div className="space-y-4">
                  <p className="text-[11px] text-center text-slate-400 font-bold uppercase tracking-widest flex items-center justify-center gap-2">
                    <ShieldCheck size={14} className="text-green-500" /> Secure Redirect to PayPal
                  </p>
                  
                  <button 
                    onClick={handleFinalRedirect}
                    className="w-full bg-gh-orange text-white py-6 rounded-[2rem] font-black uppercase tracking-widest hover:bg-orange-600 transition-all shadow-xl active:scale-95 flex items-center justify-center gap-3 group"
                  >
                    Donate via PayPal <ExternalLink size={20} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                  </button>
               </div>
               
               <button 
                onClick={() => setStep(1)} 
                className="w-full text-[10px] font-black text-slate-400 mt-4 hover:text-gh-blue transition-all uppercase tracking-[0.3em]"
              >
                ← Back to Edit
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}